import {AxoisRequestManager} from "../../stores/managers/RequestManager";
import {from} from "rxjs";
import type SitApi from "../SitApit";
import type {PageResponse} from "~/services/ApiTypes.ts";
import {makeAutoObservable} from "mobx";
import type {Category, Company, ComplexityTier, DOI, FirstPageKpis, Initiative} from "../ApiTypes";
import {DOIs} from "../ApiTypes";
import type AuthStore from "../../authentication/AuthStore";

export class InitiativeListStore {
    // filters
    search: string = '';
    my_initiatives: number = 0;
    categories: Category[] = [];
    companies: Company[] = [];
    dois: DOI[] = [];
    complexity_tiers: ComplexityTier[] = [];
    // Selected years per DOI stage gate (see cat-material's InitiativeQS.annotate_doi_years),
    // sent as one `year_doi_{stage}__in` filter per non-empty stage.
    yearsByDoi: Record<DOI, number[]> = {1: [], 2: [], 3: [], 4: [], 5: []};
    per_page = 10;
    page = 0;
    ordering = '-total_saving';
    approval_per_page = 10;
    approval_page = 0;
    // Debounce handle for the search box.
    private searchTimer?: ReturnType<typeof setTimeout>;
    // Request managers
    readonly initiativeListRM = new AxoisRequestManager<{filters: string[][]}, PageResponse<Initiative>>(
        ({filters}) => from(this.api.listInitiatives(filters)));
    readonly initiativeKpiRM = new AxoisRequestManager<{filters: string[][]}, FirstPageKpis>(
        ({filters}) => from(this.api.firstPageKpis(filters)));
    readonly initiativeApprovalListRM = new AxoisRequestManager<{filters: string[][]}, PageResponse<Initiative>>(
        ({filters}) => from(this.api.listInitiativesApproval(filters)));

    constructor(private api: SitApi, private authStore: AuthStore) {
        makeAutoObservable(this, {});
    }

    reloadData(onlyIfEmpty = false, resetPage = true) {
        if (onlyIfEmpty && (this.initiativeKpiRM.result !== undefined || this.initiativeKpiRM.busy))
            return;
        if (resetPage)
            this.page = 0;
        const filters: [string, string][] = [];
        if (this.search.trim().length > 0) filters.push(['search', this.search.trim()]);
        if (this.my_initiatives && this.authStore.user?.sit_user_profile?.id) filters.push(['owner', String(this.authStore.user.sit_user_profile.id)]);
        if (this.companies.length > 0) filters.push(['financial__companies__id__in', String(this.companies.map(c => c.id))])
        if (this.categories.length > 0) filters.push(['financial__categories__id__in', String(this.categories.map(c => c.id))])
        if (this.dois.length > 0) filters.push(['doi_status__in', String(this.dois)]);
        if (this.complexity_tiers.length > 0) filters.push(['complexity_tier__in', String(this.complexity_tiers)]);
        DOIs.forEach(doi => {
            if (this.yearsByDoi[doi].length > 0) filters.push([`year_doi_${doi}__in`, String(this.yearsByDoi[doi])]);
        });
        this.initiativeKpiRM.request({filters});
        filters.push(['page_size', String(this.per_page)])
        if (this.page > 0) filters.push(['page', String(this.page + 1)]);
        if (this.ordering !== '') filters.push(['ordering', this.ordering]);
        this.initiativeListRM.request({filters});
    }

    /** The active filters/search/ordering (no pagination) for the Excel export. */
    get exportFilters(): [string, string][] {
        const filters: [string, string][] = [];
        if (this.search.trim().length > 0) filters.push(['search', this.search.trim()]);
        if (this.my_initiatives && this.authStore.user?.sit_user_profile?.id) filters.push(['owner', String(this.authStore.user.sit_user_profile.id)]);
        if (this.companies.length > 0) filters.push(['financial__companies__id__in', String(this.companies.map(c => c.id))]);
        if (this.categories.length > 0) filters.push(['financial__categories__id__in', String(this.categories.map(c => c.id))]);
        if (this.dois.length > 0) filters.push(['doi_status__in', String(this.dois)]);
        if (this.complexity_tiers.length > 0) filters.push(['complexity_tier__in', String(this.complexity_tiers)]);
        DOIs.forEach(doi => {
            if (this.yearsByDoi[doi].length > 0) filters.push([`year_doi_${doi}__in`, String(this.yearsByDoi[doi])]);
        });
        if (this.ordering !== '') filters.push(['ordering', this.ordering]);
        return filters;
    }

    exportInitiatives() {
        return this.api.exportInitiatives(this.exportFilters);
    }

    reloadApprovalData(onlyIfEmpty = false) {
        if (onlyIfEmpty && (this.initiativeApprovalListRM.result !== undefined || this.initiativeApprovalListRM.busy))
            return;
        const filters: [string, string][] = [];
        filters.push(['page_size', String(this.approval_per_page)])
        if (this.approval_page > 0) filters.push(['page', String(this.approval_page + 1)]);
        this.initiativeApprovalListRM.request({filters});
    }

    setSearch(value: string) {
        // Update the field immediately (responsive input), debounce the reload.
        this.search = value;
        if (this.searchTimer) clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.reloadData(), 350);
    }

    setMyInitiatives(value: number) {
        this.my_initiatives = value;
        this.reloadData();
    }

    setCategories(value: Category[]) {
        this.categories = value;
        this.reloadData();
    }

    setCompanies(value: Company[]) {
        this.companies = value;
        this.reloadData();
    }

    setDois(value: DOI[]) {
        this.dois = value;
        this.reloadData();
    }

    setComplexityTiers(value: ComplexityTier[]) {
        this.complexity_tiers = value;
        this.reloadData();
    }

    setYearsForDoi(doi: DOI, value: number[]) {
        this.yearsByDoi[doi] = value;
        this.reloadData();
    }

    setPerPage(value: number) {
        if (value > 0) {
            this.per_page = value;
            this.reloadData();
        }
    }

    setPage(value: number) {
        if (value >= 0) {
            this.page = value;
            this.reloadData(false, false);
        }
    }

    setOrdering(value: string) {
        this.ordering = value;
        this.reloadData();
    }

    setApprovalPerPage(value: number) {
        if (value > 0) {
            this.approval_per_page = value;
            this.approval_page = 0;
            this.reloadApprovalData();
        }
    }

    setApprovalPage(value: number) {
        if (value >= 0) {
            this.approval_page = value;
            this.reloadApprovalData();
        }
    }
}